"use client";

import { useEffect, useState } from "react";
import { api, type AnnouncementRow } from "@/lib/api";

/**
 * EPIC-012 STORY-916 — site-wide announcement banner.
 *
 * Renders above the BetaBanner. Polls every 5 minutes to pick up
 * newly-scheduled announcements without requiring a page reload
 * (closed-beta scale; cron'd post-frequency means polling is
 * cheaper than a websocket subscription).
 *
 * Severity drives the visual:
 *   info     → blue, regular weight
 *   warning  → amber, bold
 *   critical → red, bold + larger
 *
 * Multiple active rows stack vertically. Per-user dismiss is not
 * wired in v1 — if you really need to dismiss it, hard-delete or
 * window-expire from /admin/communications.
 */

const POLL_INTERVAL_MS = 5 * 60_000;

export function AnnouncementBanner() {
  const [rows, setRows] = useState<AnnouncementRow[]>([]);

  useEffect(() => {
    let cancelled = false;
    let pollHandle: ReturnType<typeof setInterval> | null = null;

    async function load() {
      try {
        const next = await api.listActiveAnnouncements();
        if (!cancelled) setRows(next);
      } catch {
        // Soft-fail — banner is non-essential UX. Try again on the
        // next poll tick.
      }
    }

    void load();
    pollHandle = setInterval(() => void load(), POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      if (pollHandle) clearInterval(pollHandle);
    };
  }, []);

  if (rows.length === 0) return null;

  return (
    <div className="flex flex-col">
      {rows.map((row) => (
        <Banner key={row.id} row={row} />
      ))}
    </div>
  );
}

function Banner({ row }: { row: AnnouncementRow }) {
  // 2026-05-25 founder direction: info-severity uses the v2 forest
  // hero palette (#2d3b2e bg + #F1ECE2 cream text) so it reads as
  // part of the same family as BetaBanner. Critical + warning keep
  // their existing high-contrast schemes for distinguishability.
  const tone =
    row.severity === "critical"
      ? {
          background: "var(--color-no)",
          color: "white",
          fontWeight: 700,
        }
      : row.severity === "warning"
        ? {
            background:
              "color-mix(in srgb, var(--color-status-caution) 22%, transparent)",
            color: "var(--color-status-caution)",
            fontWeight: 700,
          }
        : {
            // 2026-05-25 — info-severity matches the v2 BetaBanner
            // typography 1:1: mono font, 11px, weight 500, letter
            // spacing 0.05em, line-height 1.4. Critical + warning
            // keep the existing sans / heavier weight so they stand
            // out as alerts.
            background: "#2d3b2e",
            color: "#F1ECE2",
            fontWeight: 500,
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.05em",
            lineHeight: 1.4,
            textTransform: "uppercase" as const,
          };
  const fontSize =
    row.severity === "critical"
      ? "13px"
      : row.severity === "warning"
        ? "12px"
        : "11px";
  return (
    <div
      role={row.severity === "critical" ? "alert" : "status"}
      className="px-4 py-1.5 text-center"
      style={{ ...tone, fontSize }}
    >
      {row.title && (
        <strong className="mr-1.5 uppercase tracking-[0.5px]">
          {row.title}
        </strong>
      )}
      <span>{row.body}</span>
    </div>
  );
}
